import { useState, useEffect, useCallback } from 'react';
import { Layout, Tooltip } from 'antd';
import { useLocation, NavLink } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SunOutlined,
  MoonOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import SidebarNav from './SidebarNav';
import NotificationsMenu from './NotificationsMenu';
import UserProfileMenu from './UserProfileMenu';
import SearchBar from './SearchBar';
import CommandPalette from './CommandPalette';
import { useTheme } from '../../contexts/ThemeContext';
import { AiChatButton, AiChatPanel } from '../ai/AiChatDrawer';
import styles from './Main.module.scss';

const { Sider, Content } = Layout;
const SIDEBAR_KEY = 'silos_sidebar_collapsed';

const Main = ({ children }) => {
  const { pathname } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      localStorage.setItem(SIDEBAR_KEY, String(!prev));
      return !prev;
    });
  }, []);

  // Global keyboard shortcut: Ctrl/Cmd + K → palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <Layout className={`${styles.layout} ${isDark ? styles.layoutDark : ''}`}>
      {/* ── Mobile overlay ── */}
      {mobileOpen && (
        <div className={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={240}
        collapsedWidth={64}
        className={`${styles.sider} ${mobileOpen ? styles.siderMobileOpen : ''}`}
      >
        {/* Logo */}
        <div className={styles.siderLogo}>
          <NavLink to="/" className={styles.siderLogoContent} onClick={() => setMobileOpen(false)}>
            {collapsed
              ? <img src="/logo_transparent.png" alt="Silos" className={styles.logoIcon} />
              : <img src="/logo_text.png" alt="Silos" className={styles.logoText} />
            }
          </NavLink>
          <button
            className={styles.siderCloseBtn}
            onClick={() => setMobileOpen(false)}
            aria-label="Fermer le menu"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Nav */}
        <div className={styles.siderNav}>
          <SidebarNav collapsed={collapsed} onNavigate={() => setMobileOpen(false)} />
        </div>

        {/* Icon-only collapse toggle pinned at bottom */}
        <Tooltip title={collapsed ? 'Développer' : 'Réduire'} placement="right">
          <button className={styles.collapseBtn} onClick={toggleCollapse} aria-label="Toggle sidebar">
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </Tooltip>
      </Sider>

      {/* ── Main area ── */}
      <Layout className={`${styles.mainArea} ${collapsed ? styles.mainAreaCollapsed : ''}`}>

        {/* Topbar */}
        <header className={styles.topbar}>
          {/* Mobile burger */}
          <button
            className={styles.mobileBurger}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Ouvrir le menu"
          >
            {mobileOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          </button>

          {/* Search bar (desktop) */}
          <div className={styles.topbarSearch}>
            <SearchBar />
          </div>

          {/* Spacer */}
          <div className={styles.topbarSpacer} />

          {/* Quick actions trigger (Cmd+K) */}
          <Tooltip title="Recherche rapide (⌘K)">
            <button className={styles.actionBtn} onClick={() => setPaletteOpen(true)} aria-label="Recherche rapide">
              <SearchOutlined />
              <span className={styles.cmdKHint}>⌘K</span>
            </button>
          </Tooltip>

          {/* Dark mode toggle */}
          <Tooltip title={isDark ? 'Mode clair' : 'Mode sombre'}>
            <button className={styles.actionBtn} onClick={toggleTheme} aria-label="Changer le thème">
              {isDark ? <SunOutlined /> : <MoonOutlined />}
            </button>
          </Tooltip>

          {/* Notifications */}
          <NotificationsMenu />

          {/* Profile */}
          <UserProfileMenu />
        </header>

        {/* Content */}
        <Content className={styles.content}>
          <div className={styles.contentInner}>{children}</div>
        </Content>
      </Layout>

      {/* ── Command Palette ── */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* ── AI Chat ── */}
      <AiChatButton />
      <AiChatPanel />
    </Layout>
  );
};

Main.propTypes = { children: PropTypes.node.isRequired };
export default Main;
